import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

import { getPendingGiftRedirect } from "@/utils/pendingGiftRedirect";
import { getPendingEventRedirect } from "@/utils/pendingEventBooking";

export function RoleBasedRouter() {
  const { user, userRole, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasNavigated, setHasNavigated] = useState(false);

  useEffect(() => {
    console.log("RoleBasedRouter - Auth state:", {
      user: !!user,
      userRole,
      isLoading,
      hasNavigated,
      pathname: location.pathname,
      emailConfirmed: !!user?.email_confirmed_at,
    });

    // Prevent multiple navigations
    if (hasNavigated) {
      console.log("RoleBasedRouter - Already navigated, skipping");
      return;
    }

    // Wait for auth to finish loading
    if (isLoading) {
      console.log("RoleBasedRouter - Still loading, waiting...");
      return;
    }

    // Check for verification success parameters in the URL
    const params = new URLSearchParams(location.search);
    const hasVerificationSuccess =
      params.get("verification_success") === "true";

    // Handle no user case
    if (!user) {
      console.log("RoleBasedRouter - No user found, redirecting to login");
      toast.error("Please log in to access your dashboard");
      navigate("/login", { replace: true });
      setHasNavigated(true);
      return;
    }

    // Handle user with unverified email
    if (!user.email_confirmed_at) {
      console.log("RoleBasedRouter - User email not verified");
      navigate("/verify-email", {
        replace: true,
        state: { email: user.email, role: userRole },
      });
      setHasNavigated(true);
      return;
    }

    // Handle user without role - wait for role to be assigned with timeout
    if (!userRole) {
      console.log(
        "RoleBasedRouter - User verified but no role found yet, waiting...",
      );

      // For OAuth users without role, redirect to selection page immediately
      const isOAuthUser =
        user.app_metadata?.provider && user.app_metadata.provider !== "email";
      if (isOAuthUser) {
        console.log(
          "RoleBasedRouter - OAuth user without role, redirecting to role selection",
        );
        navigate("/select-account-type", { replace: true });
        setHasNavigated(true);
        return;
      }

      // Add timeout to prevent infinite waiting (15 seconds)
      const waitTimeout = setTimeout(() => {
        if (!userRole) {
          console.error(
            "RoleBasedRouter - Timeout waiting for role after 15 seconds",
          );
          toast.error(
            "Unable to load your profile. Please try logging in again or contact support.",
          );
          navigate("/", { replace: true });
          setHasNavigated(true);
        }
      }, 15000);

      // Cleanup timeout when component unmounts or role is found
      return () => clearTimeout(waitTimeout);
    }

    // Determine the correct dashboard route based on role
    let dashboardRoute = "/dashboard/fan"; // default fallback

    switch (userRole) {
      case "artist":
        dashboardRoute = "/dashboard/artist";
        break;
      case "sponsor":
        dashboardRoute = "/dashboard/sponsor";
        break;
      case "fan":
        dashboardRoute = "/dashboard/fan";
        break;
      case "admin":
        dashboardRoute = "/dashboard/admin";
        break;
      default:
        console.warn("RoleBasedRouter - Unknown role:", userRole);
        dashboardRoute = "/dashboard/fan";
    }

    // Check for pending gift or event redirect first
    const pendingRedirect =
      getPendingGiftRedirect() || getPendingEventRedirect();
    const finalRoute = pendingRedirect || dashboardRoute;

    console.log("RoleBasedRouter - Navigating to:", finalRoute);

    // Navigate to the appropriate destination
    if (hasVerificationSuccess) {
      console.log("RoleBasedRouter - Verification success");
      toast.success("Email verified successfully! Welcome to your dashboard.");
    }

    navigate(finalRoute, { replace: true });
    setHasNavigated(true);
  }, [user, userRole, isLoading, navigate, location.search, hasNavigated]);

  return null;
}
